import React, { useEffect, useMemo, useState } from "react";
import { PageHeader } from "@/components/Layout";
import MatchCard from "@/components/MatchCard";
import api, { apiErr } from "@/lib/api";
import { fmtDate, fmtDateTime } from "@/lib/format";
import { useApp } from "@/context/AppContext";
import { ClipboardList, CheckCircle2, Send, Pencil } from "lucide-react";
import { toast } from "sonner";

const FILTERS = [
  { key: "all", label: "Tutte" },
  { key: "SERIE_A", label: "Serie A", type: "comp" },
  { key: "UCL", label: "Champions", type: "comp" },
  { key: "scheduled", label: "In programma", type: "status" },
  { key: "live", label: "In corso", type: "status" },
  { key: "finished", label: "Concluse", type: "status" },
];

export default function Calendar() {
  const { user, hasActiveSub } = useApp();
  const [matches, setMatches] = useState([]);
  const [preds, setPreds] = useState({});
  const [drafts, setDrafts] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [comp, setComp] = useState("all");
  const [status, setStatus] = useState("scheduled");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const params = {};
    if (comp !== "all") params.competition = comp;
    if (status !== "all") params.status = status;
    const { data } = await api.get("/matches", { params });
    setMatches(data);
    if (user) {
      try {
        const { data: mine } = await api.get("/predictions/mine");
        const map = {};
        mine.forEach((p) => { map[p.match_id] = p; });
        setPreds(map);
      } catch (e) { /* */ }
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [comp, status, user]);

  const handleConfirmed = (matchId, pred) => {
    setPreds((prev) => ({ ...prev, [matchId]: pred }));
    setDrafts((prev) => { const n = { ...prev }; delete n[matchId]; return n; });
  };

  const handleSelect = (matchId, choice) => {
    setDrafts((prev) => {
      const n = { ...prev };
      if (choice) n[matchId] = choice; else delete n[matchId];
      return n;
    });
  };

  const submitAll = async () => {
    const items = Object.entries(drafts).map(([match_id, choice]) => ({ match_id, choice }));
    if (!items.length) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/predictions/batch", { items });
      setPreds((prev) => {
        const n = { ...prev };
        data.saved.forEach((p) => { n[p.match_id] = p; });
        return n;
      });
      setDrafts((prev) => {
        const n = { ...prev };
        data.saved.forEach((p) => { delete n[p.match_id]; });
        return n;
      });
      if (data.errors?.length) toast.warning(`Inviati ${data.saved.length} pronostici, ${data.errors.length} non salvati`);
      else toast.success(`Schedina inviata: ${data.saved.length} pronostici confermati!`);
    } catch (e) { toast.error(apiErr(e)); } finally { setSubmitting(false); }
  };

  const mmap = useMemo(() => {
    const m = {}; matches.forEach((x) => { m[x.id] = x; }); return m;
  }, [matches]);

  const slip = useMemo(() => {
    const rows = {};
    Object.entries(preds).forEach(([mid, p]) => {
      if (mmap[mid] && !mmap[mid].locked) rows[mid] = { match: mmap[mid], choice: p.choice, status: "confermato" };
    });
    Object.entries(drafts).forEach(([mid, choice]) => {
      if (mmap[mid] && !mmap[mid].locked) rows[mid] = { match: mmap[mid], choice, status: "bozza" };
    });
    return Object.values(rows).sort((a, b) => new Date(a.match.kickoff) - new Date(b.match.kickoff));
  }, [preds, drafts, mmap]);

  const draftCount = Object.keys(drafts).length;

  const grouped = useMemo(() => {
    const g = {};
    matches.forEach((m) => {
      const day = fmtDate(m.kickoff);
      (g[day] = g[day] || []).push(m);
    });
    return g;
  }, [matches]);

  return (
    <div>
      <PageHeader overline="Serie A · Champions League" title="Calendario" subtitle="Pronostica 1-X-2 sulle partite. I pronostici si chiudono 15 minuti prima del calcio d'inizio.">
        <div className="flex flex-wrap gap-2 mt-6">
          {FILTERS.map((f) => {
            const active = (f.type === "comp" && comp === f.key) || (f.type === "status" && status === f.key) || (f.key === "all" && comp === "all" && status === "all");
            return (
              <button key={f.key} data-testid={`filter-${f.key}`}
                onClick={() => {
                  if (f.key === "all") { setComp("all"); setStatus("all"); }
                  else if (f.type === "comp") setComp(active ? "all" : f.key);
                  else setStatus(active ? "all" : f.key);
                }}
                className={`px-4 py-2 rounded-full text-sm font-medium border transition ${active ? "bg-gold-300 text-ink-900 border-gold-300" : "border-ink-600 text-slate-300 hover:border-gold-300/50"}`}>
                {f.label}
              </button>
            );
          })}
        </div>
      </PageHeader>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        {!user && (
          <div className="surface-elevated p-4 mb-6 text-sm text-slate-300">
            Accedi e attiva l'abbonamento per inviare i tuoi pronostici.
          </div>
        )}
        {user && !hasActiveSub && (
          <div className="surface-elevated p-4 mb-6 text-sm text-gold-300 border-gold-300/30">
            Abbonamento non attivo: puoi consultare il calendario ma non inviare pronostici.
          </div>
        )}
        {user && hasActiveSub && slip.length > 0 && (
          <div className="surface-elevated p-4 mb-6 border-gold-300/30" data-testid="prediction-slip">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <ClipboardList size={18} className="text-gold-300" />
              <h3 className="font-heading text-lg font-bold uppercase text-white">La tua schedina</h3>
              <span className="ml-auto text-sm text-muted-foreground" data-testid="slip-count">
                {slip.filter((r) => r.status === "confermato").length} confermati
                {draftCount > 0 && <span className="text-gold-300"> · {draftCount} in sospeso</span>}
              </span>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              {slip.map((r) => (
                <div key={r.match.id} className="flex items-center justify-between bg-ink-900 rounded-lg px-3 py-2 text-sm">
                  <div className="min-w-0">
                    <div className="text-white truncate">{r.match.home_team} <span className="text-muted-foreground">vs</span> {r.match.away_team}</div>
                    <div className="text-xs text-muted-foreground">{fmtDateTime(r.match.kickoff)}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="font-heading font-black text-lg text-gold-300 w-5 text-center">{r.choice}</span>
                    {r.status === "confermato"
                      ? <CheckCircle2 size={16} className="text-status-correct" title="Confermato" />
                      : <Pencil size={14} className="text-gold-300" title="In sospeso" />}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between gap-3 mt-4 flex-wrap">
              <p className="text-xs text-muted-foreground">I pronostici restano modificabili fino a 15 minuti prima di ogni partita.</p>
              <button
                onClick={submitAll}
                disabled={draftCount === 0 || submitting}
                data-testid="submit-slip-btn"
                className="btn-gold !py-2.5 !px-5 text-sm">
                <Send size={16} /> {submitting ? "Invio…" : draftCount > 0 ? `Invia schedina (${draftCount})` : "Tutto confermato"}
              </button>
            </div>
          </div>
        )}
        {loading ? (
          <div className="text-center text-muted-foreground py-16">Caricamento partite…</div>
        ) : matches.length === 0 ? (
          <div className="surface p-10 text-center text-muted-foreground">Nessuna partita trovata per questi filtri.</div>
        ) : (
          Object.entries(grouped).map(([day, list]) => (
            <div key={day} className="mb-8">
              <h3 className="font-heading text-lg font-bold uppercase text-gold-300 mb-4 capitalize">{day}</h3>
              <div className="grid md:grid-cols-2 gap-4">
                {list.map((m) => (
                  <MatchCard key={m.id} match={m} prediction={preds[m.id]} canPredict={hasActiveSub} onConfirmed={handleConfirmed} onSelect={handleSelect} />
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
